import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  AppBar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpacityOutlinedIcon from '@mui/icons-material/OpacityOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import PieChartOutlineOutlinedIcon from '@mui/icons-material/PieChartOutlineOutlined';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import CompareArrowsOutlinedIcon from '@mui/icons-material/CompareArrowsOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { useAuth } from '../auth/AuthContext';
import { tenantFetch, formatCents, TenantInvoice, InvoiceStatus, BillDocumentLink, RedactedBillLink } from './api';
import { generateInvoicePdf } from '../utils/generateInvoicePdf';
import { isSafeRedirectUrl } from './safeRedirect';

type ChipColor = 'warning' | 'success' | 'error' | 'default';

const STATUS_MAP: Record<InvoiceStatus, { label: string; color: ChipColor; icon: React.ReactElement }> = {
  pending: { label: 'Payment due', color: 'warning', icon: <HourglassEmptyOutlinedIcon sx={{ fontSize: '14px !important' }} /> },
  paid: { label: 'Paid', color: 'success', icon: <CheckCircleOutlineIcon sx={{ fontSize: '14px !important' }} /> },
  failed: { label: 'Payment failed', color: 'error', icon: <ErrorOutlineIcon sx={{ fontSize: '14px !important' }} /> },
  returned: { label: 'Returned', color: 'error', icon: <ErrorOutlineIcon sx={{ fontSize: '14px !important' }} /> },
  void: { label: 'Void', color: 'default', icon: <RemoveCircleOutlineIcon sx={{ fontSize: '14px !important' }} /> },
  draft: { label: 'Draft', color: 'default', icon: <RemoveCircleOutlineIcon sx={{ fontSize: '14px !important' }} /> },
};

const POLL_DELAYS_MS = [2000, 4000, 8000, 16000, 16000, 16000];

function sleep(ms: number) {
  return new Promise<void>((resolve) => setTimeout(resolve, ms));
}

function StatBox({ icon, label, value, valueColor }: { icon: React.ReactNode; label: string; value: string; valueColor?: string }) {
  return (
    <Box
      sx={{
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'background.paper',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <Box sx={{ color: 'primary.main', display: 'flex' }}>{icon}</Box>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
          {label}
        </Typography>
      </Stack>
      <Typography
        variant="body1"
        sx={{ fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", color: valueColor ?? 'text.primary' }}
      >
        {value}
      </Typography>
    </Box>
  );
}

export default function TenantInvoiceDetail() {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const navigate = useNavigate();
  const { user, getIdToken } = useAuth();
  const [invoice, setInvoice] = useState<TenantInvoice | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);
  const [pollingMsg, setPollingMsg] = useState<string | null>(null);
  const [billLoading, setBillLoading] = useState(false);
  const [billError, setBillError] = useState<string | null>(null);
  const [redactedBillLoading, setRedactedBillLoading] = useState(false);
  const [redactedBillMsg, setRedactedBillMsg] = useState<string | null>(null);
  const pollCancelledRef = useRef(false);

  useEffect(() => {
    return () => { pollCancelledRef.current = true; };
  }, []);

  useEffect(() => {
    if (!user || !invoiceId) return;
    let cancelled = false;
    (async () => {
      const token = await getIdToken(false);
      if (!token || cancelled) return;
      const res = await tenantFetch(`/tenants/me/invoices/${invoiceId}`, token);
      if (cancelled) return;
      if (!res.ok) {
        setLoadError(res.status === 404 ? 'Invoice not found.' : 'Failed to load invoice.');
        return;
      }
      const data = await res.json();
      if (!cancelled) setInvoice(data);
    })();
    return () => { cancelled = true; };
  }, [user, invoiceId, getIdToken]);

  const handlePay = async () => {
    if (!invoiceId || !invoice) return;

    if (invoice.paymentUrl) {
      // Defense-in-depth: only follow a backend-supplied URL if it passes the
      // payment-host allowlist (br-k6c). Never blindly trust paymentUrl.
      if (!isSafeRedirectUrl(invoice.paymentUrl)) {
        setPayError('Invalid payment link');
        return;
      }
      window.location.href = invoice.paymentUrl;
      return;
    }

    setPaying(true);
    setPayError(null);
    try {
      const token = await getIdToken(false);
      if (!token) throw new Error('Not signed in');

      const res = await tenantFetch(`/tenants/me/invoices/${invoiceId}/pay`, token, { method: 'POST' });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error ?? `Payment failed (${res.status})`);
      }

      for (const delay of POLL_DELAYS_MS) {
        await sleep(delay);
        if (pollCancelledRef.current) break;
        const pollToken = (await getIdToken(false)) ?? token;
        const pollRes = await tenantFetch(`/tenants/me/invoices/${invoiceId}`, pollToken);
        if (!pollRes.ok || pollCancelledRef.current) break;
        const updated: TenantInvoice = await pollRes.json();
        setInvoice(updated);
        if (updated.status !== 'pending') return;
      }
      setPollingMsg("We're processing your payment. Check back soon for an update.");
    } catch (err: any) {
      setPayError(err?.message ?? 'Payment failed. Please try again.');
    } finally {
      setPaying(false);
    }
  };

  const handleViewBill = async () => {
    if (!invoiceId) return;
    setBillLoading(true);
    setBillError(null);
    try {
      const token = await getIdToken(false);
      if (!token) throw new Error('Not signed in');

      const res = await tenantFetch(`/tenants/me/invoices/${invoiceId}/bill`, token);
      if (!res.ok) {
        // 404 (not found / no source document) and any other failure surface a
        // user-facing notice. We intentionally do not echo backend error text.
        throw new Error('Bill document not available');
      }

      const { url }: BillDocumentLink = await res.json();
      // Open the short-lived signed URL in a new tab. We never store or log it.
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      setBillError(err?.message ?? 'Bill document not available');
    } finally {
      setBillLoading(false);
    }
  };

  const handleRequestRedactedBill = async () => {
    setRedactedBillLoading(true);
    setRedactedBillMsg(null);
    try {
      const token = await getIdToken(false);
      if (!token) throw new Error('Not signed in');

      const res = await tenantFetch('/tenants/me/property-tax-bill/redacted', token);
      if (res.status === 404) {
        setRedactedBillMsg("An itemized copy isn't available yet — contact your property owner.");
        return;
      }
      if (!res.ok) {
        throw new Error('Unable to retrieve itemized refuse bill');
      }

      const { url }: RedactedBillLink = await res.json();
      // Open the short-lived signed URL in a new tab. We never store or log it.
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      setRedactedBillMsg(err?.message ?? 'Unable to retrieve itemized refuse bill');
    } finally {
      setRedactedBillLoading(false);
    }
  };

  const handleDownloadPdf = () => {
    if (!invoice) return;
    const totalCents = invoice.totalAmountCents ?? invoice.amount;
    generateInvoicePdf({
      propertyName: invoice.propertyName,
      propertyAddress: invoice.propertyAddress,
      tenantName: invoice.tenantName,
      unitNumber: invoice.unitNumber,
      period: invoice.period,
      totalAmountCents: totalCents,
      lineItems: invoice.lineItems ?? [{ label: 'Water', amountCents: totalCents }],
    });
  };

  if (loadError) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>
        <Button onClick={() => navigate('/tenant')}>← Back to invoices</Button>
      </Container>
    );
  }

  if (!invoice) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const totalCents = invoice.totalAmountCents ?? invoice.amount;
  const canPay = invoice.status === 'pending';
  const { label: statusLabel, color: statusColor, icon: statusIcon } = STATUS_MAP[invoice.status] ?? {
    label: invoice.status,
    color: 'default' as ChipColor,
    icon: <RemoveCircleOutlineIcon sx={{ fontSize: '14px !important' }} />,
  };

  const totalGal = invoice.metadata?.totalGallons;
  const tenantGal = invoice.metadata?.tenantGallons;
  const shareBasis = invoice.metadata?.shareBasis;
  const totalBillCents = invoice.totalBillCents;

  // Spec 034 — calculation transparency block. Every sub-field is optional and
  // omitted when the backend lacks the underlying data (legacy invoices).
  const calc = invoice.calculation;
  const calcRows: { label: string; value: string }[] = [];
  if (calc?.totalBillCents !== undefined) {
    calcRows.push({ label: 'Total property bill', value: formatCents(calc.totalBillCents) });
  }
  if (calc?.allocationMethod) {
    calcRows.push({ label: 'Allocation method', value: calc.allocationMethod });
  }
  if (calc?.tenantShareBasis) {
    calcRows.push({ label: 'Your share basis', value: calc.tenantShareBasis });
  }
  if (calc?.tenantAmountCents !== undefined) {
    calcRows.push({ label: 'Your amount', value: formatCents(calc.tenantAmountCents) });
  }
  const billAvailable = invoice.billAvailable === true;
  const prevCents = invoice.previousCycleAmountCents;
  const changePct =
    prevCents !== undefined && prevCents > 0
      ? ((totalCents - prevCents) / prevCents) * 100
      : undefined;

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Tooltip title="Go back">
            <IconButton
              color="inherit"
              onClick={() => navigate('/tenant')}
              edge="start"
              aria-label="Go back"
              sx={{ mr: 1, opacity: 0.8, '&:hover': { opacity: 1 } }}
            >
              <ArrowBackOutlinedIcon />
            </IconButton>
          </Tooltip>
          <Typography
            variant="h6"
            sx={{ flexGrow: 1, fontFamily: "'Fraunces', serif", fontWeight: 600, letterSpacing: '-0.5px' }}
          >
            Utilibill
          </Typography>
          <Button
            color="inherit"
            startIcon={<DownloadOutlinedIcon />}
            onClick={handleDownloadPdf}
            sx={{ opacity: 0.8, fontSize: '0.8rem', '&:hover': { opacity: 1 } }}
          >
            Download PDF
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="sm" sx={{ mt: 4, pb: 8 }}>
        {/* Hero */}
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
            <Typography
              variant="h3"
              sx={{
                fontFamily: "'JetBrains Mono', monospace",
                fontWeight: 700,
                color: 'text.primary',
                letterSpacing: '-1px',
              }}
            >
              {formatCents(totalCents)}
            </Typography>
            <Chip icon={statusIcon} label={statusLabel} color={statusColor} size="small" />
          </Stack>
          {invoice.period && (
            <Typography variant="body2" color="text.secondary">
              {invoice.period}
              {invoice.unitNumber ? ` · Unit ${invoice.unitNumber}` : ''}
            </Typography>
          )}
          {invoice.propertyName && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {invoice.propertyName}{invoice.propertyAddress ? ` — ${invoice.propertyAddress}` : ''}
            </Typography>
          )}
        </Box>

        {/* Stats grid */}
        <Grid container spacing={1.5} sx={{ mb: 3 }}>
          {tenantGal !== undefined && (
            <Grid item xs={6}>
              <StatBox
                icon={<OpacityOutlinedIcon sx={{ fontSize: 16 }} />}
                label="Your usage"
                value={`${tenantGal.toLocaleString()} gal`}
              />
            </Grid>
          )}
          {totalGal !== undefined && (
            <Grid item xs={6}>
              <StatBox
                icon={<HomeOutlinedIcon sx={{ fontSize: 16 }} />}
                label="Total property usage"
                value={`${totalGal.toLocaleString()} gal`}
              />
            </Grid>
          )}
          {invoice.period && (
            <Grid item xs={6}>
              <StatBox
                icon={<CalendarTodayOutlinedIcon sx={{ fontSize: 16 }} />}
                label="Billing period"
                value={invoice.period}
              />
            </Grid>
          )}
          {shareBasis && (
            <Grid item xs={6}>
              <StatBox
                icon={<PieChartOutlineOutlinedIcon sx={{ fontSize: 16 }} />}
                label="Share basis"
                value={shareBasis === 'equal' ? 'Equal split' : shareBasis}
              />
            </Grid>
          )}
          {totalBillCents !== undefined && (
            <Grid item xs={6}>
              <StatBox
                icon={<ReceiptOutlinedIcon sx={{ fontSize: 16 }} />}
                label="Total property bill"
                value={formatCents(totalBillCents)}
              />
            </Grid>
          )}
          {prevCents !== undefined && (
            <Grid item xs={6}>
              <StatBox
                icon={<CompareArrowsOutlinedIcon sx={{ fontSize: 16 }} />}
                label="Last cycle"
                value={formatCents(prevCents)}
              />
            </Grid>
          )}
          {changePct !== undefined && (
            <Grid item xs={6}>
              <StatBox
                icon={<CompareArrowsOutlinedIcon sx={{ fontSize: 16 }} />}
                label="vs last cycle"
                value={`${changePct >= 0 ? '+' : ''}${changePct.toFixed(1)}%`}
                valueColor={changePct >= 0 ? 'warning.main' : 'success.main'}
              />
            </Grid>
          )}
        </Grid>

        {/* Line items */}
        {invoice.lineItems && invoice.lineItems.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.72rem' }}
            >
              Charges breakdown
            </Typography>
            <Box
              sx={{
                mt: 1,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: 'background.paper',
              }}
            >
              <Table size="small">
                <TableBody>
                  {invoice.lineItems.map((li, i) => (
                    <TableRow key={i} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                      <TableCell sx={{ py: 1.25, color: 'text.primary' }}>{li.label}</TableCell>
                      <TableCell
                        align="right"
                        sx={{ py: 1.25, fontFamily: "'JetBrains Mono', monospace", fontWeight: 500 }}
                      >
                        {formatCents(li.amountCents)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ bgcolor: 'background.default' }}>
                    <TableCell sx={{ py: 1.25, fontWeight: 700 }}>Total</TableCell>
                    <TableCell
                      align="right"
                      sx={{ py: 1.25, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 }}
                    >
                      {formatCents(totalCents)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </Box>
        )}

        {/* How your charge was calculated (Spec 034) */}
        {calcRows.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.72rem' }}
            >
              How your charge was calculated
            </Typography>
            <Box
              sx={{
                mt: 1,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: 'background.paper',
              }}
            >
              <Table size="small">
                <TableBody>
                  {calcRows.map((row, i) => (
                    <TableRow key={i} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                      <TableCell sx={{ py: 1.25, color: 'text.secondary' }}>{row.label}</TableCell>
                      <TableCell
                        align="right"
                        sx={{ py: 1.25, fontFamily: "'JetBrains Mono', monospace", fontWeight: 500, color: 'text.primary' }}
                      >
                        {row.value}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Box>
        )}

        {/* Trash / refuse charge transparency (Spec 035) */}
        {invoice.trashDetails && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.72rem' }}
            >
              Trash / refuse charge
            </Typography>
            <Box
              sx={{
                mt: 1,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: 'background.paper',
              }}
            >
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ py: 1.25, color: 'text.secondary' }}>Total property refuse amount</TableCell>
                    <TableCell
                      align="right"
                      sx={{ py: 1.25, fontFamily: "'JetBrains Mono', monospace", fontWeight: 500, color: 'text.primary' }}
                    >
                      {formatCents(invoice.trashDetails.totalRefuseAmountCents)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ py: 1.25, color: 'text.secondary' }}>Your share</TableCell>
                    <TableCell
                      align="right"
                      sx={{ py: 1.25, fontFamily: "'JetBrains Mono', monospace", fontWeight: 500, color: 'text.primary' }}
                    >
                      {formatCents(invoice.trashDetails.tenantShareCents)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ py: 1.25, color: 'text.secondary' }}>Allocation method</TableCell>
                    <TableCell
                      align="right"
                      sx={{ py: 1.25, fontFamily: "'JetBrains Mono', monospace", fontWeight: 500, color: 'text.primary' }}
                    >
                      {invoice.trashDetails.allocationMethod}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ py: 1.25, color: 'text.secondary' }}>Billing period</TableCell>
                    <TableCell
                      align="right"
                      sx={{ py: 1.25, fontFamily: "'JetBrains Mono', monospace", fontWeight: 500, color: 'text.primary' }}
                    >
                      {invoice.trashDetails.period.startDate} – {invoice.trashDetails.period.endDate}
                    </TableCell>
                  </TableRow>
                  {invoice.trashDetails.cityFormula && (
                    <TableRow>
                      <TableCell sx={{ py: 1.25, color: 'text.secondary' }}>City rate formula</TableCell>
                      <TableCell
                        align="right"
                        sx={{ py: 1.25, fontFamily: "'JetBrains Mono', monospace", fontWeight: 500, color: 'text.primary', wordBreak: 'break-word' }}
                      >
                        {invoice.trashDetails.cityFormula}
                      </TableCell>
                    </TableRow>
                  )}
                  {invoice.trashDetails.canCount !== undefined && (
                    <TableRow>
                      <TableCell sx={{ py: 1.25, color: 'text.secondary' }}>Trash cans for your unit</TableCell>
                      <TableCell
                        align="right"
                        sx={{ py: 1.25, fontFamily: "'JetBrains Mono', monospace", fontWeight: 500, color: 'text.primary' }}
                      >
                        {invoice.trashDetails.canCount}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
            <Alert
              icon={<InfoOutlinedIcon fontSize="inherit" />}
              severity="info"
              sx={{ mt: 1.5 }}
            >
              This refuse charge is a landlord pass-through from the city property tax bill — it is not a direct city charge to you.
            </Alert>
            <Box sx={{ mt: 1.5 }}>
              {redactedBillMsg && (
                <Alert severity="info" sx={{ mb: 1.5 }}>{redactedBillMsg}</Alert>
              )}
              <Button
                variant="outlined"
                fullWidth
                startIcon={
                  redactedBillLoading
                    ? <CircularProgress size={16} color="inherit" />
                    : <DeleteOutlineOutlinedIcon />
                }
                onClick={() => void handleRequestRedactedBill()}
                disabled={redactedBillLoading}
                aria-label="Request itemized refuse bill in a new tab"
              >
                {redactedBillLoading ? 'Opening…' : 'Request itemized refuse bill'}
              </Button>
            </Box>
          </Box>
        )}

        {/* Original source bill (Spec 034) */}
        <Box sx={{ mb: 3 }}>
          {billError && <Alert severity="error" sx={{ mb: 1.5 }}>{billError}</Alert>}
          {billAvailable ? (
            <Button
              variant="outlined"
              fullWidth
              startIcon={
                billLoading ? <CircularProgress size={16} color="inherit" /> : <DescriptionOutlinedIcon />
              }
              onClick={() => void handleViewBill()}
              disabled={billLoading}
              aria-label="View original water bill in a new tab"
            >
              {billLoading ? 'Opening…' : 'View original bill'}
            </Button>
          ) : (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
              Source bill not available for this invoice.
            </Typography>
          )}
        </Box>

        {/* Required disclosure notice (Spec 034 — San Diego ordinance) */}
        {invoice.disclosure && (
          <Alert
            icon={<InfoOutlinedIcon fontSize="inherit" />}
            severity="info"
            sx={{ mb: 3 }}
          >
            {invoice.disclosure}
          </Alert>
        )}

        <Divider sx={{ mb: 3 }} />

        {/* Download */}
        <Button
          variant="outlined"
          fullWidth
          startIcon={<DownloadOutlinedIcon />}
          onClick={handleDownloadPdf}
          sx={{ mb: 2 }}
        >
          Download invoice PDF
        </Button>

        {payError && <Alert severity="error" sx={{ mb: 2 }}>{payError}</Alert>}
        {pollingMsg && <Alert severity="info" sx={{ mb: 2 }}>{pollingMsg}</Alert>}

        {canPay && (
          <Button
            variant="contained"
            fullWidth
            onClick={() => void handlePay()}
            disabled={paying}
            size="large"
          >
            {paying ? 'Processing…' : 'Authorize payment'}
          </Button>
        )}

        {(invoice.status === 'failed' || invoice.status === 'returned') && (
          <Button
            variant="outlined"
            color="error"
            fullWidth
            onClick={() => navigate('/tenant/setup')}
            size="large"
          >
            Resolve payment issue
          </Button>
        )}
      </Container>
    </>
  );
}
